import React, { useState, useContext, useEffect } from 'react';
import {
	Button,
	Paper,
	Typography,
	TableContainer,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Table,
	IconButton,
	Stack,
	Box
} from '@mui/material';
import { isEmpty } from 'lodash';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { deleteToken, deleteUserData, getToken } from '../../../api/Account';
import { DataContext } from '../../../context/DataContext';

import { isAccess, simpleMessage } from '../../../helpers/Helpers';

import NoData from '../../../components/NoData';
import { PaginationComponent } from '../../../components/PaginationComponent';
import SmallModal from '../../../components/modals/SmallModal';

import Swal from 'sweetalert2';
import { deleteGarantiaAsync, getCreditGarantiasAsync } from '../../../api/Credits';
import AddGarantia from './garantiaComponents/AddGarantia';
import EditGarantia from './garantiaComponents/EditGarantia';
import { monoFont } from '../../../theme';

const money = (n) => new Intl.NumberFormat('es-NI', { style: 'currency', currency: 'NIO' }).format(n);

const CreditGarantias = ({ id }) => {
	const token = getToken();
	const [garantiaList, setGarantiaList] = useState([]);
	const [selectedGarantia, setSelectedGarantia] = useState([]);

	const [localReload, setLocalReload] = useState(false);

	const { setIsLoading, setIsLogged, setIsUnautorized, access } = useContext(DataContext);

	const [searchTerm] = useState('');

	const [addGarantiaModal, setAddGarantiaModal] = useState(false);
	const [editGarantiaModal, setEditGarantiaModal] = useState(false);

	const withSearch = garantiaList.filter((val) => {
		if (searchTerm === '') {
			return val;
		} else if (val.fullName.toString().includes(searchTerm)) {
			return val;
		}
		return false;
	});

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			const result = await getCreditGarantiasAsync(token, id);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}
			setIsLoading(false);
			setGarantiaList(result.data.result);
		})();
	}, [localReload]);

	const [currentPage, setCurrentPage] = useState(1);
	const [itemsperPage] = useState(10);
	const indexLast = currentPage * itemsperPage;
	const indexFirst = indexLast - itemsperPage;
	const currentItem = withSearch.slice(indexFirst, indexLast);
	const paginate = (pageNumber) => setCurrentPage(pageNumber);

	const deleteGarantia = async (item) => {
		Swal.fire({
			title: '¿Estas Seguro?',
			text: 'No podras revertir esto...!',
			icon: 'warning',
			showCancelButton: true,

			confirmButtonColor: '#2979ff',
			cancelButtonColor: '#f50057',

			confirmButtonText: 'Eliminar!',
			cancelButtonText: 'Cancelar',
			customClass: {
				popup: 'border'
			}
		}).then((result) => {
			if (result.isConfirmed) {
				(async () => {
					setIsLoading(true);
					const result = await deleteGarantiaAsync(token, item.id);
					if (!result.statusResponse) {
						setIsLoading(false);
						if (result.error === 'eX01') {
							setIsUnautorized(true);
							return;
						}
						simpleMessage(result.error.response.data.message, 'error');
						return;
					}
					if (result.error === 'Unauthorized') {
						setIsLoading(false);
						deleteUserData();
						deleteToken();
						setIsLogged(false);
						return;
					}
					setIsLoading(false);
					setLocalReload(!localReload);
					Swal.fire('Eliminada!', 'Garantia Eliminada.', 'success');
				})();
			}
		});
	};

	return (
		<div>
			<Paper sx={{ overflow: 'hidden' }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '16px 20px',
						borderBottom: '1px solid',
						borderColor: 'divider',
						flexWrap: 'wrap',
						gap: '10px'
					}}
				>
					<Typography sx={{ fontSize: 15, fontWeight: 600 }}>Garantías del crédito</Typography>
					{isAccess(access, 'CREDITS CREATE') ? (
						<Button
							onClick={() => setAddGarantiaModal(true)}
							variant="contained"
							color="primary"
							size="small"
							startIcon={<AddRoundedIcon sx={{ fontSize: 17 }} />}
						>
							Agregar
						</Button>
					) : null}
				</Box>

				{isEmpty(currentItem) ? (
					<NoData />
				) : (
					<Stack spacing={0}>
						<TableContainer sx={{ overflowX: 'auto' }}>
							<Table size="small" sx={{ minWidth: 560 }}>
								<TableHead>
									<TableRow>
										<TableCell>#</TableCell>
										<TableCell>Artículo</TableCell>
										<TableCell>Marca</TableCell>
										<TableCell>Color</TableCell>
										<TableCell align="right">V.Estimado</TableCell>
										{isAccess(access, 'CREDITS UPDATE') || isAccess(access, 'CREDITS DELETE') ? (
											<TableCell align="center">Acciones</TableCell>
										) : null}
									</TableRow>
								</TableHead>
								<TableBody>
									{currentItem.map((row) => (
										<TableRow key={row.id}>
											<TableCell sx={{ fontFamily: monoFont, color: 'text.disabled' }}>{row.id}</TableCell>
											<TableCell sx={{ fontWeight: 500 }}>{row.articulo}</TableCell>
											<TableCell>{row.marca}</TableCell>
											<TableCell>{row.color}</TableCell>
											<TableCell align="right" sx={{ fontFamily: monoFont, fontWeight: 600 }}>
												{money(row.valorEstimado)}
											</TableCell>
											{isAccess(access, 'CREDITS UPDATE') || isAccess(access, 'CREDITS DELETE') ? (
												<TableCell align="center">
													<Stack direction={'row'} spacing={0.5} justifyContent="center">
														{isAccess(access, 'CREDITS UPDATE') ? (
															<IconButton
																size="small"
																onClick={() => {
																	setSelectedGarantia(row);
																	setEditGarantiaModal(true);
																}}
															>
																<EditRoundedIcon sx={{ fontSize: 17 }} />
															</IconButton>
														) : null}
														{isAccess(access, 'CREDITS DELETE') ? (
															<IconButton size="small" sx={{ color: 'error.main' }} onClick={() => deleteGarantia(row)}>
																<DeleteRoundedIcon sx={{ fontSize: 17 }} />
															</IconButton>
														) : null}
													</Stack>
												</TableCell>
											) : null}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
						<Box sx={{ padding: '10px 20px 14px' }}>
							<PaginationComponent data={withSearch} paginate={paginate} itemsperPage={itemsperPage} />
						</Box>
					</Stack>
				)}
			</Paper>

			<SmallModal titulo={'Agregar Garantia'} isVisible={addGarantiaModal} setVisible={setAddGarantiaModal}>
				<AddGarantia setShowModal={setAddGarantiaModal} id={id} localReload={localReload} setLocalReload={setLocalReload} />
			</SmallModal>

			<SmallModal titulo={'Editar Garantia'} isVisible={editGarantiaModal} setVisible={setEditGarantiaModal}>
				<EditGarantia
					setShowModal={setEditGarantiaModal}
					selectedGarantia={selectedGarantia}
					setSelectedGarantia={setSelectedGarantia}
					localReload={localReload}
					setLocalReload={setLocalReload}
				/>
			</SmallModal>
		</div>
	);
};

export default CreditGarantias;
